import { Component, HostListener, ViewChild, ElementRef } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { OrdersService } from 'src/app/e-commerce/orders/orders.service'
import { AppUsersService } from '../services/app-users.service'
import { Observable } from 'rxjs'

export interface ordersTableColumns {
  id: string
  date: string
  time: any
  payment_method: string
  user_id: string
  status: string
  total: string
  humId: number
  action: string
}

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent {
  ELEMENT_DATA: ordersTableColumns[] = []
  filteredOrders: ordersTableColumns[] = []
  displayedOrders: ordersTableColumns[] = []
  pageIndex = 0
  pageSize = 10

  orders: any = {}
  location: string = ''
  typeValue: string = ''
  public userIdFromRoute: any = []
  userData: any
  allowUpdateStatus = false
  searchText: string = ''
  totalOrdersPrice: any
  adminUser: boolean = false
  newRole: any
  roleDropdownOpen = false
  dropdownTop = 0
  dropdownLeft = 0
  dropdownWidth = 0

  @ViewChild('roleTrigger') roleTrigger!: ElementRef

  readonly roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'orderManager', label: 'Order Manager' },
    { value: 'productManager', label: 'Product Manager' },
    { value: 'stockManager', label: 'Stock Manager' },
  ]

  getRoleOptionLabel(value: string): string {
    return this.roleOptions.find(o => o.value === value)?.label ?? value
  }

  toggleRoleDropdown(event: Event) {
    event.stopPropagation()
    if (!this.roleDropdownOpen) {
      const rect = this.roleTrigger.nativeElement.getBoundingClientRect()
      this.dropdownTop = rect.bottom + 4
      this.dropdownLeft = rect.left
      this.dropdownWidth = rect.width
    }
    this.roleDropdownOpen = !this.roleDropdownOpen
  }

  selectRole(value: string, event: Event) {
    event.stopPropagation()
    this.newRole = value
    this.roleDropdownOpen = false
  }

  @HostListener('document:click')
  closeRoleDropdown() {
    this.roleDropdownOpen = false
  }
  constructor(
    private route: ActivatedRoute,
    public orderService: OrdersService,
    private builderService: BuilderService,
    private ordersService: OrdersService,
    private appUsersService: AppUsersService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap
    this.userIdFromRoute = routeParams.get('userId')

    if (this.userIdFromRoute) {
      this.appUsersService.getUser(this.builderService.selectedProject, this.userIdFromRoute).subscribe((data: any) => {
        if (data && Object.keys(data).length > 0) {
          this.userData = data
          this.adminUser = false
          this.userData['updated_at'] = data.updated_at ? this.convertUserDate(data.updated_at) : ''
          this.location = data.location ? data.location.trim() : ''
          this.typeValue = data.type ? data.type.trim() : ''
          this.loadOrders()
        } else {
          this.appUsersService.getStacksUser(this.userIdFromRoute).subscribe((data) => {
            this.userData = data
            this.adminUser = true
            this.newRole = this.userData.user_role
          })
        }
      })
    }
  }

  loadOrders() {
    this.ordersService.getUserOrders(this.userIdFromRoute).then((orders) => {
      this.orders = orders
      this.renderOrders(orders).subscribe(() => {
        this.totalOrdersPrice = this.ELEMENT_DATA.reduce((sum, element) => sum + Number(element.total), 0)
        this.filteredOrders = [...this.ELEMENT_DATA]
        this.updateDisplayedOrders()
      })
    })
  }

  updateDisplayedOrders() {
    const start = this.pageIndex * this.pageSize
    this.displayedOrders = this.filteredOrders.slice(start, start + this.pageSize)
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredOrders.length / this.pageSize) || 1
  }

  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--
      this.updateDisplayedOrders()
    }
  }

  nextPage() {
    if (this.pageIndex < this.getTotalPages() - 1) {
      this.pageIndex++
      this.updateDisplayedOrders()
    }
  }

  convertUserDate = (dateString: string): string => {
    const isoDate = dateString.split('T')[0] // "2024-12-24"
    const parts = isoDate.split('-') // ["2024", "12", "24"]
    return `${parts[2]}-${parts[1]}-${parts[0]}` // "24-12-2024"
  }
  renderOrders = (result: any[]) => {
    return new Observable<any>((observer) => {
      this.builderService.showUpdateProgress = false
      this.ELEMENT_DATA = (result || []).map((element) => ({
        id: element.order_id,
        payment_method: element.payment_method,
        user_id: element.user,
        status: element.status,
        total: element.total,
        date: element.date,
        time: element.date,
        humId: element.humId,
        action: ''
      }))

      observer.next()
      observer.complete()
    })
  }

  editOrder = (id: string) => {
    this.router.navigate([`/orders/single-order/${id}`], { relativeTo: this.route })
  }

  /**
   * Applies a filter to the data source of the user table.
   * The filter is applied to the status, payment_method,total and date columns of the table.
   * The filter value is taken from the `searchText` property, which is trimmed and converted to lowercase.
   */
  applyFilter() {
    const filterValue = this.searchText ? this.searchText.trim().toLowerCase() : ''
    this.filteredOrders = this.ELEMENT_DATA.filter(
      (data) =>
        data.status.toLowerCase().includes(filterValue) ||
        data.payment_method?.toLowerCase().includes(filterValue) ||
        data.date?.includes(filterValue) ||
        data.total?.toString().includes(filterValue)
    )
    this.pageIndex = 0
    this.updateDisplayedOrders()
  }

  /**
   * Sends the new role to the backend to update the meta
   */
  updateNewRole = () => {
    this.builderService.showUpdateProgress = true
    this.appUsersService.updateUserRole(this.userIdFromRoute, this.newRole).subscribe((response: any) => {
      this.builderService.showUpdateProgress = false
      this.router.navigate(['/users'])
    })
  }

  approveUser() {
    this.userData = { ...this.userData, status: 'approved' }
  }

  saveUser() {
    const updatedUser = {
      ...this.userData,
      location: this.location.trim(),
      user_type: this.typeValue.trim(),
      type: this.typeValue.trim()
    }

    this.appUsersService.updateUser(this.builderService.selectedProject, this.userIdFromRoute, updatedUser).subscribe(() => {
      this.router.navigate(['/users'])
    })
  }
}
